import type { Cell } from '../game/model/types';
import { cellKey } from '../game/model/cells';

export interface BoardMask {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly cells: readonly Cell[];
  has(x: number, y: number): boolean;
}

function createMask(id: string, width: number, height: number, cells: readonly Cell[]): BoardMask {
  if (cells.length === 0) {
    throw new Error(`Mask "${id}" must contain at least one cell.`);
  }

  const keys = new Set(cells.map(cellKey));
  const visited = new Set<string>();
  const first = cells[0];
  if (first !== undefined) {
    const pending = [first];
    while (pending.length > 0) {
      const cell = pending.pop();
      if (cell === undefined || visited.has(cellKey(cell))) {
        continue;
      }
      visited.add(cellKey(cell));
      for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) {
        const neighbor = { x: cell.x + dx, y: cell.y + dy };
        const neighborKey = cellKey(neighbor);
        if (keys.has(neighborKey) && !visited.has(neighborKey)) {
          pending.push(neighbor);
        }
      }
    }
  }
  if (visited.size !== cells.length) {
    throw new Error(`Mask "${id}" must be a single 4-connected component.`);
  }

  return {
    id,
    width,
    height,
    cells,
    has: (x, y) => keys.has(cellKey({ x, y })),
  };
}

export function rectMask(id: string, width: number, height: number): BoardMask {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    throw new Error(`Mask "${id}" dimensions must be positive integers.`);
  }
  const cells: Cell[] = [];
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      cells.push({ x, y });
    }
  }
  return createMask(id, width, height, cells);
}

export function parseMaskArt(id: string, art: string): BoardMask {
  const lines = art
    .trim()
    .split(/\r?\n/u)
    .map((line) => line.trim());
  const width = Math.max(0, ...lines.map((line) => line.length));
  const cells: Cell[] = [];

  lines.forEach((line, y) => {
    for (let x = 0; x < width; x += 1) {
      const character = line[x] ?? '.';
      if (character === '#') {
        cells.push({ x, y });
      } else if (character !== '.') {
        throw new Error(`Mask "${id}" contains invalid character "${character}" at ${x},${y}.`);
      }
    }
  });

  return createMask(id, width, lines.length, cells);
}

export const heartMask = parseMaskArt('heart', `
...####..####...
.#############.
################
################
.##############.
..############..
...##########...
....########....
.....######.....
......####......
.......##.......
`);

export const butterflyMask = parseMaskArt('butterfly', `
..#####....#####..
.#######..#######.
##################
##################
.################.
..##############..
....##########....
..##############..
.################.
##################
.#######..#######.
..#####....#####..
`);

// Fish (normal tier motif) — 350 cells, 26x26
export const fishMask = parseMaskArt('fish', `
.........##
........#####
........#######
.......########
.......#########
.......#########
......##########........##
......##########.......###
.....###########......####
...##############....####
..################..#####
.########################
#########################
########################
#########################
#########################
.#################..#####
..################...####
....############......###
......##########.......###
........########........##
........#######
........#######
.........######
.........#####
..........###
`);

// Crescent moon (normal tier motif) — 340 cells, 22x45
export const moonMask = parseMaskArt('moon', `
..............####
............######
..........#######
........#######
.......#######
......#######
.....#######
....#######
....#######
...#######
...#######
..#######
..#######
.#######
.#######
.#######
########
#######
#######
#######
#######
#######
#######
#######
#######
#######
########
########
########
.#######
.########
.########
..########
..########
...########
...########
....########
.....########
.....#########
......#########
.......##########
........##########
..........###########
............##########
...............#######
`);

// Mushroom (normal tier motif) — 372 cells, 25x24
export const mushroomMask = parseMaskArt('mushroom', `
........#########
.....###############
....#################
...###################
..#####################
.#######################
.#######################
#########################
#########################
#########################
#########################
..#####################
..#####################
.........#######
........#########
........#########
........#########
........#########
........#########
........#########
.......###########
.......###########
.......###########
........#########
`);

// Apple (normal tier motif) — 360 cells, 23x23
export const appleMask = parseMaskArt('apple', `
.................##
............##..#####
............##.######
...........##.#####
...........##.####
......##...##..##
...#####...##.#####
..#######.###########
.####################
.#####################
#######################
#######################
#######################
#######################
#######################
#######################
######################
.#####################
.####################
..##################
....###############
......###########
.......########
`);

// Five-pointed star (hard tier motif) — 521 cells, 35x33
export const starMask = parseMaskArt('star', `
................###
................###
................###
...............#####
...............#####
...............#####
..............#######
..............#######
.............#########
.............#########
.............#########
............###########
###################################
###################################
.#################################
..###############################
...#############################
....###########################
......#######################
.......#####################
........###################
.........#################
.........#################
........###################
........###################
........###################
........###################
.......##########.##########
.......#########...#########
.......#######.......#######
.......######.........######
......#####.............#####
......####...............####
`);

// Crown with a jewel cavity (hard tier motif) — 544 cells, 28x27
export const crownMask = parseMaskArt('crown', `
.............##
............####
...........######
...........######
..##.......######.......##
.####......######......####
.####.....########.....####
.#####....########....#####
.#####....########....#####
.#####....########....#####
.######..##########..######
.######..##########..######
.######..##########..######
.#######.##########.#######
.##########################
.##########################
.##########################
############################
############################
############################
############....############
############....############
############....############
#############..#############
############################
############################
.##########################
`);

// Rocket with a porthole cavity (hard tier motif) — 521 cells, 29x36
export const rocketMask = parseMaskArt('rocket', `
.............###
.............###
............#####
...........#######
...........#######
..........#########
.........###########
.........###########
........#############
.......###############
......#################
......#######...#######
......######.....######
......######.....######
......######.....######
......#######...#######
......#################
......#################
......#################
......#################
......#################
......#################
.....###################
.....###################
....#####################
...#######################
...#######################
..#########################
..#####..###########..#####
.######...#########...######
.####....###########....####
####.....###########.....####
##.......###########.......##
.........###########
..........#########
..........#########
`);

// Crab (hard tier motif) — 530 cells, 33x32
export const crabMask = parseMaskArt('crab', `
..####.....................####
.######...................######
.#######.................#######
..######.................######
......##.................##
......##.................##
......##....###...###....##
...#####...####...####...#####
..#####....####...####....#####
..######....###...###....######
.....###....#########....###
.....####..###########..####
......###.#############.###
......#####################
.......###################
.......###################
........#################
.......###################
.....#######################
...###########################
..######.###############.######
.#####..#################..#####
.###....####.###.###.####....###
####...####.####.####.####...####
###...####..####.####..####...###
###...###...###...###...###...###
##....###..####...####..###....##
.....####..###.....###..####
.....###...###.....###...###
.....###...###.....###...###
.....###..####.....####..###
..........###.......###
`);
